'use client';

import Link from '@components/Link';
import { Paragraph } from '@components/ui/Paragraph';
import { Accordion } from '@ngg/components';
import type {
    DesignerPageFragment,
    ProductPageInfoParametricFragment,
    ProductPageParametricFragment,
} from '@server/gql/graphql';
import { Fragment, useMemo } from 'react';
import type { Dictionary } from 'types/common';
import type { Product, Variant } from 'types/product';

import { FEATURE_FLAGS } from '@/config/featureFlags';
import variantProperties, {
    propertySuffixMap,
} from '@/config/variantProperties';

import Icon from './icon';

/**
 *
 * @deprecated
 * used as a fallback until contenful properties are setup in full
 */

const getAttributeWithSuffix = ({
    property,
    attribute,
}: {
    property: string;
    attribute: string;
}) => {
    if (propertySuffixMap[property]) {
        return `${attribute} ${propertySuffixMap[property]}`;
    }

    return attribute;
};

/**
 *
 * @deprecated - used as a fallback until contenful properties are setup in full
 *
 * @todo Refactor. Too much going on here.
 */

const _ProductAttributesList = ({
    entity,
    dictionary,
    locale,
    brandSlug,
    designers,
}: {
    // What is entity and why is it typed as Record<string, any>?
    entity: Record<string, any>;
    dictionary: Dictionary;
    locale?: string;
    brandSlug?: string | null;
    designers?: (DesignerPageFragment | null)[];
}) => (
    <div className="grid grid-cols-[max-content,_auto] gap-x-4 gap-y-2 text-sm">
        {variantProperties.map((property) => {
            if (property === 'norce_brand' && entity[property]) {
                return (
                    <Fragment key={'norce_brand' + entity[property]}>
                        <Paragraph className="font-medium">
                            {dictionary[property] ?? property}:
                        </Paragraph>
                        <Link
                            className="hover:underline"
                            locale={locale}
                            href={brandSlug}>
                            {entity[property]}
                        </Link>
                    </Fragment>
                );
            }
            if (
                property === 'norce_designer' &&
                entity[property] &&
                !!designers?.length
            ) {
                return (
                    <Fragment key={'norce_designer' + entity[property]}>
                        <Paragraph className="font-medium">
                            {dictionary[property] ?? property}:
                        </Paragraph>
                        <span>
                            {designers.map((designer, index: number) => {
                                if (!designer?.title) {
                                    return null;
                                }

                                return (
                                    <div key={index}>
                                        <Link
                                            key={designer.title + index}
                                            className="hover:underline"
                                            locale={locale}
                                            href={designer.slug}>
                                            {designer.title}
                                        </Link>
                                        {index < designers.length - 1
                                            ? ', '
                                            : ''}
                                    </div>
                                );
                            })}
                        </span>
                    </Fragment>
                );
            }
            if (entity[property] && !Array.isArray(entity[property])) {
                const attribute = entity[property];
                const dictKey = property === 'id' ? 'SKU' : property;

                return (
                    <Fragment key={property + attribute}>
                        <Paragraph className="font-medium">
                            {dictionary[dictKey] ?? property}:
                        </Paragraph>
                        <Paragraph>
                            {getAttributeWithSuffix({
                                attribute,
                                property,
                            })}
                        </Paragraph>
                    </Fragment>
                );
            }
        })}
    </div>
);

/**
 * @deprecated - Deprecated in favor of Parametrics in ProductPageInformation.tsx
 */
const ProductAttributesList = ({
    entity,
    locale,
    brandSlug,
    designers,
    parametrics,
}: {
    entity: Record<string, any>;
    locale?: string;
    brandSlug?: string | null;
    designers?: (DesignerPageFragment | null)[];
    parametrics?: ProductPageParametricFragment[];
}) => (
    <div className="grid grid-cols-[max-content,_auto] gap-x-4 gap-y-2 text-sm">
        {parametrics?.map((parametric) => {
            if (!parametric?.attributeName) {
                return null;
            }

            if (
                parametric.attributeName === 'norce_brand' &&
                entity[parametric.attributeName]
            ) {
                return (
                    <Fragment key={parametric.attributeName}>
                        <Paragraph className="font-medium">
                            {parametric.title ?? parametric.attributeName}:
                        </Paragraph>
                        <Link
                            className="hover:underline"
                            locale={locale}
                            href={brandSlug}>
                            {entity[parametric.attributeName]}
                        </Link>
                    </Fragment>
                );
            }
            if (
                parametric.attributeName === 'norce_designer' &&
                entity[parametric.attributeName].length > 0 &&
                designers?.length === 0
            ) {
                return (
                    <Fragment key={parametric.attributeName}>
                        <Paragraph className="font-medium">
                            {parametric.title ?? parametric.attributeName}:
                        </Paragraph>
                        <span>
                            {designers.map((designer, index: number) => {
                                if (!designer?.title) {
                                    return null;
                                }

                                return (
                                    <div key={index}>
                                        <Link
                                            key={designer.sys.id + index}
                                            className="hover:underline"
                                            locale={locale}
                                            href={designer.slug}>
                                            {designer.title}
                                        </Link>
                                        {index < designers.length - 1
                                            ? ', '
                                            : ''}
                                    </div>
                                );
                            })}
                        </span>
                    </Fragment>
                );
            }
            if (
                entity[parametric.attributeName] &&
                !Array.isArray(entity[parametric.attributeName])
            ) {
                return (
                    <Fragment key={parametric.attributeName}>
                        <Paragraph className="font-medium">
                            {parametric.title ?? parametric.attributeName}:
                        </Paragraph>
                        <Paragraph>
                            {parametric.prefix}
                            {entity[parametric.attributeName]}
                            {parametric.suffix}
                        </Paragraph>
                    </Fragment>
                );
            }
        })}
    </div>
);

/**
 * @deprecated - Deprecated in favor of Parametrics in ProductPageInformation.tsx and ProductPageParametrics.tsx
 */
export default function Properties({
    dictionary,
    variant,
    product,
    className,
    locale,
    brandSlug,
    designers,
    parametricBlocks,
}: {
    dictionary: Dictionary;
    variant?: Variant;
    product: Product;
    className?: string;
    locale?: string;
    brandSlug?: string | null;
    designers?: (DesignerPageFragment | null)[];
    parametricBlocks?: (ProductPageInfoParametricFragment | null)[];
}) {
    const entity: Record<string, any> = useMemo(
        () => ({ ...product, ...variant }),
        [product, variant],
    );

    if (
        !parametricBlocks?.length ||
        !FEATURE_FLAGS.product.contentfulParametrics
    )
        return (
            <Accordion
                title={dictionary?.productInformation}
                IconComponent={Icon}
                WrapperComponent="div"
                transitionProps={{ appear: false }}
                classNames={{
                    wrapper: className,
                    button: 'text-left items-center leading-4 p-4 font-medium rounded-none',
                    panel: 'p-4',
                }}
                defaultOpen>
                <_ProductAttributesList
                    entity={entity}
                    dictionary={dictionary}
                    locale={locale}
                    brandSlug={brandSlug}
                    designers={designers}
                />
                <p className="mt-4 text-xs text-grey-300 md:hidden">
                    {dictionary.imageDisclaimerText}
                </p>
            </Accordion>
        );

    return parametricBlocks?.map((entry) => {
        if (entry?.__typename !== 'Block') {
            return null;
        }

        const parametrics = entry.componentsCollection?.items.filter(
            (item) => item?.__typename === 'Parametric',
        );

        if (
            parametrics?.some((parametric) => {
                // make sure attributeName is set in contentful
                if (typeof parametric.attributeName !== 'string') return false;
                // parametrics without value should not be shown
                // parametrics with multiple values should not be shown since no variant is selected
                if (typeof entity[parametric.attributeName] === 'string')
                    return true;
                if (typeof entity[parametric.attributeName] === 'number')
                    return true;
                if (
                    Array.isArray(entity[parametric.attributeName]) &&
                    entity[parametric.attributeName]?.length === 1
                )
                    return true;
                // if value is missing for all parametrics of a block, hide the entire block
                return false;
            })
        ) {
            return (
                <div key={entry.sys.id}>
                    <div className="grid grid-cols-[max-content,_auto] gap-x-4 gap-y-2 text-sm">
                        <ProductAttributesList
                            brandSlug={brandSlug}
                            entity={entity}
                            locale={locale}
                            designers={designers}
                            parametrics={parametrics}
                        />
                    </div>
                </div>
            );
        }
        return null;
    });
}
